import { nextTick, onUnmounted, ref } from "vue";

const SEARCH_DELAY = 300;
const SCROLL_THRESHOLD = 40;

const getRows = (data) => {
  if (Array.isArray(data?.results)) {
    return data.results;
  }
  return Array.isArray(data) ? data : [];
};

/**
 * Options of a Multiselect loaded from a paginated endpoint - searched on the
 * server and extended with the next page while the dropdown is scrolled, like
 * cp-input-ui-select with data-with-loading in the old admin panel.
 *
 * fetchPage({ page, query }) resolves with the axios response of the list,
 * toOption maps one result to { name, value } and getSelected returns the
 * selected value(s), which stay among the options while searching.
 */
export const usePagedSelect = ({ fetchPage, toOption, getSelected }) => {
  const options = ref([]);
  const loading = ref(false);
  let page = 1;
  let query = "";
  let hasMore = false;
  let requestId = 0;
  let searchTimeout = null;
  let dropdownEl = null;

  const applyPage = (response, pageNumber, append) => {
    const found = getRows(response?.data).map(toOption);

    if (append) {
      const known = new Set(options.value.map((option) => option.value));
      options.value = [
        ...options.value,
        ...found.filter((option) => !known.has(option.value)),
      ];
    } else {
      const selected = []
        .concat(getSelected ? getSelected() : [])
        .filter((value) => value !== null && value !== undefined);
      const foundValues = new Set(found.map((option) => option.value));
      const kept = options.value.filter(
        (option) =>
          selected.includes(option.value) && !foundValues.has(option.value)
      );
      options.value = [...kept, ...found];
    }

    page = pageNumber;
    hasMore = Boolean(response?.data?.next);
  };

  const load = (pageNumber, append) => {
    const current = ++requestId;
    loading.value = true;

    return fetchPage({ page: pageNumber, query })
      .then((response) => {
        // A newer search replaced this request.
        if (current === requestId) {
          applyPage(response, pageNumber, append);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (current === requestId) {
          loading.value = false;
        }
      });
  };

  // Fills the first page from a response fetched elsewhere, so several selects
  // can share one initial request.
  const setFirstPage = (response) => {
    query = "";
    applyPage(response, 1, false);
  };

  const reload = () => {
    query = "";
    return load(1, false);
  };

  const onSearch = (value) => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      query = value || "";
      load(1, false);
    }, SEARCH_DELAY);
  };

  const loadMore = () => {
    if (!hasMore || loading.value) {
      return;
    }
    load(page + 1, true);
  };

  const onScroll = (event) => {
    const el = event.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_THRESHOLD) {
      loadMore();
    }
  };

  const unbindScroll = () => {
    if (dropdownEl) {
      dropdownEl.removeEventListener("scroll", onScroll);
      dropdownEl = null;
    }
  };

  // Bound to the Multiselect @open event, which passes the component instance.
  const bindScroll = (select$) => {
    nextTick(() => {
      unbindScroll();
      dropdownEl = select$?.$el?.querySelector(".multiselect-dropdown") || null;
      if (dropdownEl) {
        dropdownEl.addEventListener("scroll", onScroll);
      }
    });
  };

  onUnmounted(() => {
    unbindScroll();
    clearTimeout(searchTimeout);
  });

  return {
    options,
    loading,
    reload,
    setFirstPage,
    onSearch,
    bindScroll,
    unbindScroll,
  };
};
